import useCreateStripeApi from '@client/page/management/stripe/stripe-api-key/logic/use-create-stripe-api.ts';
import { useCompanyId } from '@packages/core/company/focused-company-context.ts';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';
import useUpdateStripeApi from '../logic/use-update-stripe-api';
import useValidateStripeAPIKey from '../logic/use-validate-stripe-api-key';

enum ApiKeyState {
  apiKeys = 0,
  webhook = 1,
  success = 2,
  error = 3,
}

interface ApiKeyInput {
  name: string;
  secretKey: string;
  publishableKey: string;
  webhookSecret: string;
}

export default function CreateStripeApiDialog(props: { onClose: () => void }) {
  const { t } = useTranslate();
  const companyId = useCompanyId();
  const { showAlert } = useNotificationCenter();

  const [create, { loading }] = useCreateStripeApi();
  const [update] = useUpdateStripeApi();

  const [validate, { loading: validateLoading }] = useValidateStripeAPIKey();

  const [apiKey, setApiKey] = useState<ApiKeyInput>({
    name: '',
    secretKey: '',
    publishableKey: '',
    webhookSecret: '',
  });

  const [emptyValidation, setEmptyValidation] = useState<Record<string, boolean>>({
    name: false,
    secretKey: false,
    publishableKey: false,
    webhookSecret: false,
  });

  const [validation, setValidation] = useState<Record<string, boolean>>({
    secretKey: false,
    publishableKey: false,
  });

  const [apiKeyState, setApiKeyState] = useState<ApiKeyState>(ApiKeyState.apiKeys);
  const [createdApiKeyId, setCreatedApiKeyId] = useState<string>('');

  const validateSubmitForm = async (): Promise<boolean> => {
    try {
      const result = await validate({
        variables: {
          publishableKey: apiKey.publishableKey,
          secretKey: apiKey.secretKey,
        },
      });

      const queryValidations: Record<string, boolean> = {
        secretKey: false,
        publishableKey: false,
      };

      if (result.data?.validateStripeAPIKey.isValid) {
        return true;
      }

      if (result.data?.validateStripeAPIKey.details.secretKey) {
        queryValidations.secretKey = true;
      }

      if (result.data?.validateStripeAPIKey.details.publishableKey) {
        queryValidations.publishableKey = true;
      }

      setValidation(queryValidations);
      return false;
    } catch (error) {
      showAlert({
        status: 'error',
        message: t('stripe.apiKey.validation.error.message', 'Failed to validate API keys'),
        title: t('stripe.apiKey.validation.error.title', 'Validation Error'),
      });
      return false;
    }
  };

  const isPassValidation = (fields: (keyof ApiKeyInput)[]): boolean => {
    let isValid = true;
    const validations: Record<string, boolean> = {};

    fields.forEach((key) => {
      if (apiKey[key] === '') {
        validations[key] = true;
        isValid = false;
      }
    });

    setEmptyValidation((prev) => ({ ...prev, ...validations }));
    return isValid;
  };

  const onSubmitApiKeys = async () => {
    if (!isPassValidation(['name', 'secretKey', 'publishableKey'])) return;

    const isValid = await validateSubmitForm();
    if (!isValid) return;

    try {
      const result = await create({
        variables: {
          input: {
            companyId,
            name: apiKey.name,
            secretKey: apiKey.secretKey,
            publishableKey: apiKey.publishableKey,
            webhookSecret: '', // Gửi rỗng cho lần đầu
          },
        },
      });

      setCreatedApiKeyId(result.data?.createStripeAPIKey?.id ?? '');
      setApiKeyState(ApiKeyState.webhook);
      showAlert({
        status: 'success',
        message: t('stripe.apiKey.alert.apiKeys.success.message', 'API Keys saved successfully'),
        title: t('stripe.apiKey.alert.apiKeys.success.title', 'Success'),
      });
    } catch (e) {
      setApiKeyState(ApiKeyState.error);
      showAlert({
        status: 'error',
        message: t('stripe.apiKey.alert.error.message', 'An error occurred during creation'),
        title: t('stripe.apiKey.alert.error.title', 'Error'),
      });
    }
  };

  const onSubmitWebhook = async () => {
    if (!isPassValidation(['webhookSecret'])) return;

    try {
      await update({
        variables: {
          id: createdApiKeyId,
          input: {
            webhookSecret: apiKey.webhookSecret,
          },
        },
      });

      setApiKeyState(ApiKeyState.success);
      showAlert({
        status: 'success',
        message: t('stripe.apiKey.alert.webhook.success.message', 'Webhook saved successfully'),
        title: t('stripe.apiKey.alert.webhook.success.title', 'Success'),
      });
    } catch (e) {
      setApiKeyState(ApiKeyState.error);
      showAlert({
        status: 'error',
        message: t('stripe.apiKey.alert.error.message', 'An error occurred during webhook update'),
        title: t('stripe.apiKey.alert.error.title', 'Error'),
      });
    }
  };

  return (
    <Card>
      <CardHeader title={t('stripe.apiKey.header', 'Create API Key')} onCrossButton={props.onClose} />

      <CardContent>
        {apiKeyState === ApiKeyState.apiKeys && (
          <FormLayout>
            <FormItem title={t('stripe.apiKey.name.title', 'Name')}>
              <TextInput
                type="text"
                label="name"
                value={apiKey.name}
                placeholder={t('stripe.apiKey.name.placeholder', 'Enter name')}
                name="name"
                error={emptyValidation.name ? t('stripe.apiKey.name.error', 'Name is required') : undefined}
                onChange={(e) => {
                  setApiKey((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }));
                  setEmptyValidation((prev) => ({ ...prev, name: false }));
                }}
              />
            </FormItem>

            <FormItem title={t('stripe.apiKey.secretKey.title', 'Secret Key')}>
              <TextInput
                type="text"
                label="secretKey"
                value={apiKey.secretKey}
                placeholder={t('stripe.apiKey.secretKey.placeholder', 'Enter secret key')}
                name="secretKey"
                error={
                  emptyValidation.secretKey
                    ? t('stripe.apiKey.secretKey.error', 'Secret key is required')
                    : validation.secretKey
                      ? t('stripe.apiKey.secretKey.notvalid', 'Secret key not valid')
                      : undefined
                }
                onChange={(e) => {
                  setApiKey((prev) => ({
                    ...prev,
                    secretKey: e.target.value,
                  }));
                  setEmptyValidation((prev) => ({ ...prev, secretKey: false }));
                  setValidation((prev) => ({ ...prev, secretKey: false }));
                }}
              />
            </FormItem>

            <FormItem title={t('stripe.apiKey.publishableKey.title', 'Publishable Key')}>
              <TextInput
                type="text"
                label="publishableKey"
                value={apiKey.publishableKey}
                placeholder={t('stripe.apiKey.publishableKey.placeholder', 'Enter publishable key')}
                name="publishableKey"
                error={
                  emptyValidation.publishableKey
                    ? t('stripe.apiKey.publishableKey.error', 'Publishable key is required')
                    : validation.publishableKey
                      ? t('stripe.apiKey.publishableKey.notvalid', 'Publishable key not valid')
                      : undefined
                }
                onChange={(e) => {
                  setApiKey((prev) => ({
                    ...prev,
                    publishableKey: e.target.value,
                  }));
                  setEmptyValidation((prev) => ({ ...prev, publishableKey: false }));
                  setValidation((prev) => ({ ...prev, publishableKey: false }));
                }}
              />
            </FormItem>
          </FormLayout>
        )}

        {apiKeyState === ApiKeyState.webhook && (
          <FormLayout>
            <FormItem title={t('stripe.apiKey.webhook-secretKey.title', 'Webhook Secret Key')}>
              <TextInput
                type="text"
                label="webhookSecret"
                value={apiKey.webhookSecret}
                placeholder={t('stripe.apiKey.webhook-secretKey.placeholder', 'Enter webhook secret key')}
                name="webhookSecret"
                error={
                  emptyValidation.webhookSecret
                    ? t('stripe.apiKey.webhook-secretKey.error', 'Webhook Secret key is required')
                    : undefined
                }
                onChange={(e) => {
                  setApiKey((prev) => ({
                    ...prev,
                    webhookSecret: e.target.value,
                  }));
                  setEmptyValidation((prev) => ({ ...prev, webhookSecret: false }));
                }}
              />
            </FormItem>
          </FormLayout>
        )}

        {apiKeyState === ApiKeyState.success && (
          <div className="space-y-4 text-center">
            <h2 className="text-xl font-semibold text-green-600">
              {t('stripe.apiKey.alert.success.title', 'Success')}
            </h2>
            <p className="text-sm text-gray-700">
              {t('stripe.apiKey.alert.success.message', 'Successfully created API Key')}
            </p>
          </div>
        )}

        {apiKeyState === ApiKeyState.error && (
          <div className="space-y-4 text-center">
            <h2 className="text-xl font-semibold text-red-600">{t('stripe.apiKey.alert.error.title', 'Error')}</h2>
            <p className="text-sm text-gray-700">
              {t('stripe.apiKey.alert.error.message', 'An error occurred during creation')}
            </p>
          </div>
        )}
      </CardContent>

      <CardFooter>
        {apiKeyState === ApiKeyState.apiKeys && (
          <div className="flex justify-end space-x-2">
            <SecondaryButton onClick={props.onClose}>{t('stripe.apiKey.cancel', 'Cancel')}</SecondaryButton>
            <PrimaryButton onClick={() => void onSubmitApiKeys()} loading={loading || validateLoading}>
              {t('stripe.apiKey.submit', 'Submit')}
            </PrimaryButton>
          </div>
        )}

        {apiKeyState === ApiKeyState.webhook && (
          <div className="flex justify-end space-x-2">
            <SecondaryButton onClick={props.onClose}>{t('stripe.apiKey.cancel', 'Cancel')}</SecondaryButton>
            <PrimaryButton onClick={() => void onSubmitWebhook()} loading={loading}>
              {t('stripe.apiKey.submit', 'Submit')}
            </PrimaryButton>
          </div>
        )}

        {(apiKeyState === ApiKeyState.success || apiKeyState === ApiKeyState.error) && (
          <div className="flex justify-end">
            <PrimaryButton onClick={props.onClose}>{t('stripe.apiKey.close', 'Close')}</PrimaryButton>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
